import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  Chip,
  Box,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../../services/api';
import toastUtils from '../../../utils/toastUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3)
  },
  stepContent: {
    padding: theme.spacing(2)
  },
  configCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  progressCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  tableItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  mappingGrid: {
    marginTop: theme.spacing(2)
  },
  logContainer: {
    maxHeight: 300,
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.85rem'
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  },
  warningChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText
  }
}));

const ImportData = () => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Configuração da conexão
  const [dbConfig, setDbConfig] = useState({
    host: 'localhost',
    port: '5432',
    database: '',
    username: '',
    password: ''
  });
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Configuração das tabelas
  const [tablesConfig, setTablesConfig] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);

  // Progress da migração
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [migrationLogs, setMigrationLogs] = useState([]);
  const [migrationRunning, setMigrationRunning] = useState(false);

  // Validação
  const [validationResults, setValidationResults] = useState(null);

  // Dialogs
  const [viewMappingDialog, setViewMappingDialog] = useState({ open: false, table: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  // Buscar configuração atual ao carregar
  useEffect(() => {
    loadCurrentConfig();
  }, []);

  const loadCurrentConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/migration/config');
      if (response.data.success) {
        const config = response.data.config;
        setDbConfig({
          host: config.oldDatabase.host,
          port: config.oldDatabase.port.toString(),
          database: config.oldDatabase.database,
          username: config.oldDatabase.username,
          password: ''
        });
        setTablesConfig(config.tables);
        setSelectedTables(config.tables.map(t => t.targetTable));
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setLoading(true);
      const response = await api.get('/migration/test-connection');

      if (response.data.success) {
        setConnectionStatus('success');
        toastUtils.success('Conexão estabelecida com sucesso!');
        setActiveStep(1);
      } else {
        setConnectionStatus('error');
        toastUtils.error('Falha na conexão: ' + response.data.details);
      }
    } catch (error) {
      setConnectionStatus('error');
      toastUtils.error('Erro na conexão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startMigration = async () => {
    try {
      setMigrationRunning(true);
      setMigrationProgress(null);
      setMigrationLogs([]);

      const cleanBefore = confirmDialog.action === 'clean';

      const response = await api.post('/migration/start', {
        cleanBefore,
        tables: selectedTables
      });

      if (response.data.success) {
        setMigrationProgress(response.data.summary);
        setActiveStep(3);
        toastUtils.success('Migração iniciada com sucesso!');
      } else {
        toastUtils.error('Erro ao iniciar migração: ' + response.data.error);
      }
    } catch (error) {
      toastUtils.error('Erro na migração: ' + error.message);
    } finally {
      setMigrationRunning(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const validateMigration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/migration/validate');

      if (response.data.success) {
        setValidationResults(response.data.validation);
        toastUtils.success('Validação concluída!');
      } else {
        toastUtils.error('Erro na validação: ' + response.data.error);
      }
    } catch (error) {
      toastUtils.error('Erro na validação: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cleanTables = async () => {
    if (confirmDialog.action === 'clean_confirm') {
      try {
        setLoading(true);
        const response = await api.post('/migration/clean', {
          confirm: 'DELETE_ALL_DATA'
        });

        if (response.data.success) {
          toastUtils.success('Tabelas limpas com sucesso!');
        } else {
          toastUtils.error('Erro ao limpar tabelas: ' + response.data.error);
        }
      } catch (error) {
        toastUtils.error('Erro na limpeza: ' + error.message);
      } finally {
        setLoading(false);
        setConfirmDialog({ open: false, action: null });
      }
    } else {
      setConfirmDialog({ open: true, action: 'clean_confirm' });
    }
  };

  const viewTableMapping = async (tableName) => {
    try {
      const response = await api.get(`/migration/mapping/${tableName}`);
      if (response.data.success) {
        setViewMappingDialog({
          open: true,
          table: response.data.mapping
        });
      }
    } catch (error) {
      toastUtils.error('Erro ao buscar mapeamento: ' + error.message);
    }
  };

  const steps = [
    {
      label: 'Configuração da Conexão',
      content: (
        <div className={classes.stepContent}>
          <Typography variant="h6" gutterBottom>
            Configure a conexão com o banco de dados antigo
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Host"
                value={dbConfig.host}
                onChange={(e) => setDbConfig({ ...dbConfig, host: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Porta"
                value={dbConfig.port}
                onChange={(e) => setDbConfig({ ...dbConfig, port: e.target.value })}
                variant="outlined"
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Banco"
                value={dbConfig.database}
                onChange={(e) => setDbConfig({ ...dbConfig, database: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Usuário"
                value={dbConfig.username}
                onChange={(e) => setDbConfig({ ...dbConfig, username: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Senha"
                value={dbConfig.password}
                onChange={(e) => setDbConfig({ ...dbConfig, password: e.target.value })}
                variant="outlined"
                size="small"
                type="password"
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={testConnection}
              disabled={loading || !dbConfig.database || !dbConfig.username}
              startIcon={loading ? null : <PlayIcon />}
            >
              {loading ? 'Testando...' : 'Testar Conexão'}
            </Button>

            {connectionStatus === 'success' && (
              <Chip
                icon={<CheckIcon />}
                label="Conexão OK"
                className={classes.successChip}
                style={{ marginLeft: 16 }}
              />
            )}

            {connectionStatus === 'error' && (
              <Chip
                icon={<ErrorIcon />}
                label="Erro na Conexão"
                className={classes.errorChip}
                style={{ marginLeft: 16 }}
              />
            )}
          </Box>
        </div>
      )
    },
    {
      label: 'Seleção de Tabelas',
      content: (
        <div className={classes.stepContent}>
          <Typography variant="h6" gutterBottom>
            Selecione as tabelas para migração
          </Typography>

          <Alert severity="info" style={{ marginBottom: 16 }}>
            <AlertTitle>Ordem de Migração</AlertTitle>
            As tabelas serão migradas na ordem listada para respeitar relacionamentos.
          </Alert>

          {tablesConfig.map((table, index) => (
            <Card key={table.targetTable} className={classes.configCard}>
              <CardContent>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={1}>
                    <Typography variant="h6" color="primary">
                      {index + 1}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedTables.includes(table.targetTable)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTables([...selectedTables, table.targetTable]);
                            } else {
                              setSelectedTables(selectedTables.filter(t => t !== table.targetTable));
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={table.targetTable}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">
                      {table.sourceTable} → {table.targetTable}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Chip
                      size="small"
                      label={`${table.columnMappings} colunas`}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Chip
                      size="small"
                      label={`Lote: ${table.batchSize || 1000}`}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      size="small"
                      onClick={() => viewTableMapping(table.targetTable)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setActiveStep(2)}
              disabled={selectedTables.length === 0}
            >
              Continuar ({selectedTables.length} tabelas selecionadas)
            </Button>
          </Box>
        </div>
      )
    },
    {
      label: 'Confirmação',
      content: (
        <div className={classes.stepContent}>
          <Typography variant="h6" gutterBottom>
            Confirme a migração
          </Typography>

          <Alert severity="warning" style={{ marginBottom: 16 }}>
            <AlertTitle>Atenção</AlertTitle>
            A migração pode levar alguns minutos dependendo do volume de dados.
          </Alert>

          <Card className={classes.configCard}>
            <CardHeader title="Resumo da Migração" />
            <CardContent>
              <Typography variant="body2">
                <strong>Banco de origem:</strong> {dbConfig.database}@{dbConfig.host}:{dbConfig.port}
              </Typography>
              <Typography variant="body2">
                <strong>Tabelas selecionadas:</strong> {selectedTables.length}
              </Typography>
              <Box mt={2}>
                {selectedTables.map(table => (
                  <Chip
                    key={table}
                    label={table}
                    size="small"
                    style={{ margin: 4 }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          <Box mt={3}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setConfirmDialog({ open: true, action: 'clean' })}
              startIcon={<DeleteIcon />}
              style={{ marginRight: 16 }}
            >
              Limpar e Migrar
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setConfirmDialog({ open: true, action: 'migrate' })}
              startIcon={<PlayIcon />}
              disabled={migrationRunning}
            >
              {migrationRunning ? 'Migrando...' : 'Iniciar Migração'}
            </Button>
          </Box>
        </div>
      )
    },
    {
      label: 'Progresso',
      content: (
        <div className={classes.stepContent}>
          <Typography variant="h6" gutterBottom>
            Progresso da Migração
          </Typography>

          {migrationProgress && (
            <Card className={classes.progressCard}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h4" color="primary">
                      {migrationProgress.migratedRecords}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Registros Migrados
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h4" color="primary">
                      {migrationProgress.totalRecords}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total de Registros
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h4" color="primary">
                      {migrationProgress.completedTables}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tabelas Concluídas
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h4" color={migrationProgress.errorRecords > 0 ? "error" : "primary"}>
                      {migrationProgress.errorRecords}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Erros
                    </Typography>
                  </Grid>
                </Grid>

                <Box mt={3}>
                  <Typography variant="body2" gutterBottom>
                    Progresso Geral: {Math.round((migrationProgress.migratedRecords / migrationProgress.totalRecords) * 100)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(migrationProgress.migratedRecords / migrationProgress.totalRecords) * 100}
                  />
                </Box>

                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Detalhes por Tabela
                  </Typography>
                  {migrationProgress.tables && migrationProgress.tables.map(table => (
                    <Box key={table.table} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          {table.table}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Chip
                            size="small"
                            label={`${table.migrated}/${table.total}`}
                            color={table.success ? "primary" : "secondary"}
                          />
                          {table.success ? (
                            <Chip
                              size="small"
                              icon={<CheckIcon />}
                              label="Sucesso"
                              className={classes.successChip}
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<ErrorIcon />}
                              label={`${table.errors} erros`}
                              className={classes.errorChip}
                            />
                          )}
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={table.total > 0 ? (table.migrated / table.total) * 100 : 0}
                        style={{ marginTop: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={validateMigration}
              disabled={loading || migrationRunning}
              startIcon={<CheckIcon />}
              style={{ marginRight: 16 }}
            >
              Validar Migração
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={cleanTables}
              disabled={loading || migrationRunning}
              startIcon={<DeleteIcon />}
            >
              Limpar Tabelas
            </Button>
          </Box>

          {validationResults && (
            <Card className={classes.progressCard} style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resultados da Validação
                </Typography>
                {Object.entries(validationResults).map(([table, result]) => (
                  <Box key={table} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{table}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip size="small" label={`Antigo: ${result.old}`} variant="outlined" />
                      <Chip size="small" label={`Novo: ${result.new}`} variant="outlined" />
                      {result.match ? (
                        <Chip
                          size="small"
                          icon={<CheckIcon />}
                          label="OK"
                          className={classes.successChip}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<ErrorIcon />}
                          label="Divergência"
                          className={classes.errorChip}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )
    }
  ];

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Importação de Dados
      </Typography>

      <Typography variant="body1" color="textSecondary" paragraph>
        Configure e execute a migração de dados do sistema antigo para o novo sistema.
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.content}
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Dialog de Confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'clean' && 'Limpar e Migrar'}
          {confirmDialog.action === 'migrate' && 'Confirmar Migração'}
          {confirmDialog.action === 'clean_confirm' && 'Confirmar Limpeza'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'clean' &&
              'Todas as tabelas de destino serão limpas antes da migração. Esta ação não pode ser desfeita.'
            }
            {confirmDialog.action === 'migrate' &&
              'Os dados serão adicionados às tabelas existentes. Registros duplicados serão ignorados.'
            }
            {confirmDialog.action === 'clean_confirm' &&
              'ATENÇÃO: Esta ação irá apagar TODOS os dados das tabelas de destino. Esta ação é IRREVERSÍVEL.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.action === 'clean_confirm' ? cleanTables : startMigration}
            color={confirmDialog.action === 'clean_confirm' ? 'secondary' : 'primary'}
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Visualização de Mapeamento */}
      <Dialog
        open={viewMappingDialog.open}
        onClose={() => setViewMappingDialog({ open: false, table: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Mapeamento: {viewMappingDialog.table?.sourceTable} → {viewMappingDialog.table?.targetTable}
        </DialogTitle>
        <DialogContent>
          {viewMappingDialog.table?.columnMappings && (
            <div>
              {viewMappingDialog.table.columnMappings.map((mapping, index) => (
                <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    {mapping.source || '<valor padrão>'} → {mapping.target}
                  </Typography>
                  <Box>
                    {mapping.transformer && (
                      <Chip size="small" label={mapping.transformer} variant="outlined" />
                    )}
                    {mapping.defaultValue !== undefined && (
                      <Chip size="small" label={`padrão: ${mapping.defaultValue}`} variant="outlined" style={{ marginLeft: 4 }} />
                    )}
                  </Box>
                </Box>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMappingDialog({ open: false, table: null })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ImportData;